import { Link, useLocation, useSearch } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { T } from '../lib/tokens'
import { useCategories, useCategoryCounts } from '../lib/hooks'
import { CatDot } from './ui'
import { signOut, supabase } from '../lib/supabase'
import { SettingsDrawer } from './SettingsDrawer'

const NAV_ITEMS = [
  { to: '/',        icon: '◇',  label: 'すべて' },
  { to: '/ingest',  icon: '⌛', label: '未確認' },
  { to: '/search',  icon: '🔍', label: '検索' },
  { to: '/archive', icon: '□',  label: 'アーカイブ' },
  { to: '/tags',    icon: '#',  label: 'タグ' },
  { to: '/chat',    icon: '💬', label: 'チャット' },
  { to: '/chats',   icon: '✦',  label: 'チャット履歴', iconSize: 14 },
  { to: '/news',    icon: '📰', label: 'ニュース' },
]

export function Sidebar() {
  const location   = useLocation()
  // 絞り込みは URL(?cat=/?fav=) を単一ソースに。ダッシュボードと自動同期する。
  const search     = useSearch({ strict: false }) as { cat?: string; fav?: string }
  const onDash     = location.pathname === '/'
  const curCat     = search.cat ?? null
  const fav        = search.fav === '1'
  const [settingsOpen, setSettingsOpen] = useState(false)
  const { data: categories = [] }       = useCategories()
  const { data: counts = {} }           = useCategoryCounts()
  const [email, setEmail]               = useState<string | null>(null)
  const [totalCount, setTotalCount]     = useState<number>(0)

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setEmail(session?.user?.email ?? null)
    })
    supabase
      .from('ml_items')
      .select('id', { count: 'exact', head: true })
      .eq('status', 'done')
      .then(({ count }) => setTotalCount(count ?? 0))
  }, [])

  const initial = email?.[0]?.toUpperCase() ?? 'M'

  return (
    <>
      <aside style={{
        width: 220, flexShrink: 0,
        background: T.bg,
        borderRight: `1px solid ${T.border}`,
        display: 'flex', flexDirection: 'column',
        fontFamily: T.font,
      }}>
        {/* ロゴ */}
        <div style={{
          padding: '16px 14px 12px',
          display: 'flex', alignItems: 'center', gap: 10,
          borderBottom: `1px solid ${T.border}`,
        }}>
          <div style={{
            width: 26, height: 26, borderRadius: 7, flexShrink: 0,
            background: 'linear-gradient(135deg, oklch(0.72 0.16 258) 0%, oklch(0.60 0.20 278) 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontWeight: 700, fontSize: 13, fontFamily: T.fontMono,
          }}>M</div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.text, lineHeight: 1.2 }}>MemoLake</div>
            <div style={{ fontSize: 10, color: T.textMute, fontFamily: T.fontMono }}>{totalCount} items</div>
          </div>
        </div>

        {/* ナビゲーション */}
        <div style={{ padding: '10px 8px 4px' }}>
          <SidebarLabel>WORKSPACE</SidebarLabel>
          {NAV_ITEMS.map(item => (
            <SidebarRow
              key={item.to}
              to={item.to}
              // 「すべて」(/) は検索を明示クリアし、cat/fav 選択から確実に戻れるように
              search={item.to === '/' ? {} : undefined}
              icon={item.icon}
              label={item.label}
              iconSize={(item as any).iconSize}
              // 「すべて」(/) は cat/fav が無いときだけ active（カテゴリ/お気に入り選択時は外す）
              active={item.to === '/' ? (onDash && !curCat && !fav) : (location.pathname === item.to)}
            />
          ))}
          <SidebarRow to="/" search={{ fav: '1' }} icon="⭐" label="お気に入り" active={onDash && fav} />
        </div>

        {/* カテゴリ */}
        <div style={{ padding: '8px 8px 4px', flex: 1, overflow: 'hidden auto' }}>
          <SidebarLabel>
            カテゴリ
            <span style={{ color: T.textMute, fontWeight: 400, marginLeft: 4 }}>· gemini</span>
          </SidebarLabel>
          {categories.map(cat => (
            <SidebarRow
              key={cat.id}
              to="/"
              search={{ cat: cat.id }}
              icon={<CatDot catId={cat.id} size={7} />}
              label={cat.label}
              count={counts[cat.id]}
              active={onDash && curCat === cat.id}
            />
          ))}
        </div>

        {/* ユーザー・設定・ログアウト */}
        <div style={{
          padding: '10px 12px',
          borderTop: `1px solid ${T.border}`,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          {/* アバター */}
          <div style={{
            width: 24, height: 24, borderRadius: '50%',
            background: 'oklch(0.55 0.18 28)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: 10, fontWeight: 700, flexShrink: 0,
          }}>{initial}</div>

          {/* メール */}
          <div style={{
            flex: 1, overflow: 'hidden',
            fontSize: 11, color: T.textDim,
            textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{email}</div>

          {/* 設定ボタン */}
          <button
            onClick={() => setSettingsOpen(true)}
            title="設定"
            style={{
              background: 'transparent', border: 'none',
              color: T.textMute, cursor: 'pointer', fontSize: 13,
              padding: 2, lineHeight: 1, flexShrink: 0,
            }}
          >⚙</button>

          {/* ログアウトボタン */}
          <button
            onClick={signOut}
            title="ログアウト"
            style={{
              background: 'transparent', border: 'none',
              color: T.textMute, cursor: 'pointer', fontSize: 13,
              padding: 2, lineHeight: 1, flexShrink: 0,
            }}
          >⏻</button>
        </div>
      </aside>

      {/* 設定ドロワー */}
      <SettingsDrawer open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </>
  )
}

function SidebarLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontSize: 9, letterSpacing: '0.12em', color: T.textMute,
      fontFamily: T.fontMono, padding: '6px 6px 3px',
      textTransform: 'uppercase', fontWeight: 500,
    }}>{children}</div>
  )
}

function SidebarRow({
  to, search, icon, label, count, active, iconSize,
}: {
  to: string; search?: Record<string, unknown>; icon: React.ReactNode; label: string
  count?: number; active?: boolean; iconSize?: number
}) {
  return (
    <Link
      to={to}
      search={search as never}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '0 8px', height: 28, borderRadius: 5,
        background: active ? T.bgActive : 'transparent',
        color: active ? T.text : T.textDim,
        fontSize: 13, textDecoration: 'none',
        transition: 'background 0.12s',
      }}
    >
      <span style={{
        width: 14, display: 'inline-flex',
        justifyContent: 'center', fontSize: iconSize ?? 11, color: T.textDim,
      }}>{icon}</span>
      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {label}
      </span>
      {count != null && count > 0 && (
        <span style={{ fontSize: 10, color: T.textMute, fontFamily: T.fontMono }}>{count}</span>
      )}
    </Link>
  )
}
